import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useEffect, useMemo } from 'react';
import { AxiosError, AxiosResponse } from 'axios';
import { RetryApiAlert } from '../../shared/components/RetryApiAlert/RetryApiAlert';
import { PipelineAPI } from '../api/PipelineAPI';
import { IntegrationFactory } from '../factories/integrationFactory';
import { GenericPageShimmer } from '../../shimmer/GenericPageShimmer/GenericPageShimmer';
import { resolveCreatePipelineSteps } from './utils/createPipelineUtil';
import { CreatePipelineContextProvider } from './components/CreatePipelineContext';
import { getErrorMessageFromAxiosError } from '../../request/utils';
import { CreatePipelineWizard } from './CreatePipelineWizard';
import { useQuery } from '../../request/hooks/useQuery';
import { EntityUIState } from '../../request/interfaces/entity-ui-state';
import { Integration, RawIntegration } from '../../shared/interfaces/integration';
import { useCreatePipelineContext } from './hooks/useCreatePipelineContext';
import { CREATE_PIPELINE_DATA_IDS } from './dataIds';
import { RawSourceType, SourceType } from '../../shared/interfaces/source';
import { SourceAPI } from '../../sources/api/SourceAPI';
import { SourceTypeFactory } from '../../sources/SelectSourceType/factories/sourceTypesFactory';

export default function CreatePipeline() {
  const { id: pipelineId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const { isCommonSourceTypeFlow, selectedSourceType, queryParams } = useMemo(() => {
    const _queryParams = new URLSearchParams(location.search);
    const sourceTypeFromCommonSourceTypePage = _queryParams.get('source_type');

    return {
      isCommonSourceTypeFlow: !!sourceTypeFromCommonSourceTypePage,
      selectedSourceType: sourceTypeFromCommonSourceTypePage,
      queryParams: _queryParams
    };
  }, [location.search]);

  const {
    data,
    fetch: fetchIntegration,
    uiState,
    error
  } = useQuery({
    dataSource$: () => {
      const promises = [
        PipelineAPI.getIntegration(Number(pipelineId)),
        ...(isCommonSourceTypeFlow ? [SourceAPI.getSourceTypes()] : [])
      ];

      return Promise.all(promises).then(([{ data: rawIntegration }, rawSourceTypesData]) => {
        const _integration = IntegrationFactory(rawIntegration as RawIntegration);

        const { nextStep, completed } = resolveCreatePipelineSteps(
          _integration,
          isCommonSourceTypeFlow
        );

        if (completed) {
          navigate('..', { replace: true, relative: 'path' });
          return undefined;
        }

        const rawSourceType = (rawSourceTypesData?.data as RawSourceType[])?.find(
          rs => rs.type === selectedSourceType
        );

        if (selectedSourceType && !rawSourceType) {
          throw new AxiosError('Source not found!', undefined, undefined, undefined, {
            status: 400,
            data: {
              message: 'Source not found!'
            }
          } as AxiosResponse);
        }

        if (nextStep) {
          queryParams.delete('wizard_step');
          queryParams.set('wizard_step', nextStep);

          const navigationPath = {
            search: queryParams.toString()
          };

          navigate(navigationPath);
        }

        return {
          integration: _integration,
          sourceType: rawSourceType ? SourceTypeFactory(rawSourceType) : null
        };
      });
    }
  });

  useEffect(() => {
    fetchIntegration();
  }, [pipelineId]);

  const dataIds = CREATE_PIPELINE_DATA_IDS;

  return (
    <CreatePipelineContextProvider>
      {uiState === EntityUIState.LOADING ? <GenericPageShimmer dataId={dataIds.shimmer} /> : null}

      {uiState === EntityUIState.IDLE && data ? (
        <CreatePipelineInner initialPipeline={data.integration} sourceType={data.sourceType} />
      ) : null}

      {uiState === EntityUIState.ERRORED && error ? (
        <RetryApiAlert
          title='Failed to fetch the Pipeline'
          subTitle={getErrorMessageFromAxiosError(error)}
          secondaryCTAProps={{
            children: 'Retry',
            onClick: fetchIntegration
          }}
          className='h-100'
          dataId={dataIds.retry}
        />
      ) : null}
    </CreatePipelineContextProvider>
  );
}

function CreatePipelineInner({
  initialPipeline,
  sourceType
}: {
  initialPipeline: Integration;
  sourceType: SourceType | null;
}) {
  const { updateState, pipeline } = useCreatePipelineContext();

  useEffect(() => {
    updateState({
      pipeline: initialPipeline,
      sourceType,
      source: initialPipeline.source,
      destination: initialPipeline.destination
    });
  }, [initialPipeline]);

  return pipeline ? <CreatePipelineWizard isCommonSourceTypeFlow={!!sourceType} /> : null;
}
